import React from "react"
import CommonImage from "@/components/common/Image"
import styled from "styled-components"
import { getAspectRatioFromImage, getCssSizeValue, getFontSize, getPaddingsClassFromData, isStringEmpty } from "@/utils/utils"
import Reveal from "@/animations/Reveal"
import { WIDTH } from "@/constants"
import GridContainer from "@/components/common/GridContainer"
import { BREAKPOINTS } from "@/styles/themeSettings"
import HeaderDescriptionBlock from "@/components/HeaderDescriptionBlock"

const Wrapper = styled.div`
	position: relative;
`

const ImageContainer = styled(GridContainer)`
	position: relative;

	.text {
		font-size: ${getFontSize(100)};
		font-weight: ${({ theme }) => theme.fontWeight.medium};
		color: white;
	}

	&.width {
		&-${WIDTH.FULL}, &-${WIDTH.MEDIUM}, &-${WIDTH.SMALL}, &-${WIDTH.VERY_SMALL} {
			padding: unset;

			.image {
				grid-column: 1 / calc(var(--grid-count) + 1);
			}
		}
	}

	@media (min-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
		&.width {
			&-${WIDTH.FULL} {
				.image {
					grid-column: 1 / calc(var(--grid-count) + 1);
				}
			}

			&-${WIDTH.MEDIUM} {
				.image {
					grid-column: 2 / calc(var(--grid-count));
				}
			}

			&-${WIDTH.SMALL} {
				.image {
					grid-column: 4 / calc(var(--grid-count) - 2);
				}
			}

			&-${WIDTH.VERY_SMALL} {
				.image {
					grid-column: 5 / calc(var(--grid-count) - 3);
				}
			}
		}
	}
`

const Image = styled(CommonImage)`
	width: 100%;
	display: flex;
	justify-content: center;
	align-items: center;
	z-index: 0;
	height: auto;
	aspect-ratio: ${({ $aspectRatio }) => $aspectRatio};
`

const CaptionWrapper = styled.div`
	--bottom: ${getCssSizeValue(80)};
	position: absolute;
	bottom: calc(var(--pb) + var(--bottom));
	left: 0;
	z-index: 3;
	width: 100%;
`

const CaptionGridContainer = styled(GridContainer)``

const Caption = styled(Reveal)`
	display: flex;
	align-content: center;
	background: ${({ theme }) => theme.colors.periwinkle};
	padding: ${getCssSizeValue(40)} ${getCssSizeValue(64)};
`

const CAPTION_SIZES = {
	[WIDTH.FULL]: {
		INDENT: 0,
		WIDTH_COL: 12,
	},
	[WIDTH.MEDIUM]: {
		INDENT: 1,
		WIDTH_COL: 10,
	},
	[WIDTH.SMALL]: {
		INDENT: 3,
		WIDTH_COL: 9,
	},
	[WIDTH.VERY_SMALL]: {
		INDENT: 4,
		WIDTH_COL: 7,
	},
}

function getDataForImageWithCaption(data) {
	const image = data?.image?.image || {}
	const caption = data?.caption || ""
	const width = data?.width || WIDTH.FULL
	const aspectRatio = getAspectRatioFromImage(image)
	const paddings = getPaddingsClassFromData(data)

	return { image, caption, width, aspectRatio, paddings }
}

const ImageWithCaption = ({ data }) => {
	const { image, caption, width, aspectRatio, paddings } = getDataForImageWithCaption(data)
	const { INDENT, WIDTH_COL } = CAPTION_SIZES[width]

	const renderCaption = !isStringEmpty(caption)

	return (
		<Wrapper className={paddings}>
			<HeaderDescriptionBlock headerDescription={data?.headerDescription} />
			<ImageContainer className={`no-margin padding width-${width}`}>
				<Image
					src={image.url}
					alt={image.alt}
					focalPoint={image.focalPoint}
					placeholder={image.blurUpThumb}
					$aspectRatio={aspectRatio}
				/>
			</ImageContainer>
			{renderCaption && (
				<CaptionWrapper>
					<CaptionGridContainer className={`no-margin ${width !== WIDTH.FULL ? "padding" : ""} indent-desktop-${INDENT}`}>
						<Caption onInView className={`typo-borna typo-borna-100 col-desktop-${WIDTH_COL}`}>
							{caption}
						</Caption>
					</CaptionGridContainer>
				</CaptionWrapper>
			)}
		</Wrapper>
	)
}

export default ImageWithCaption
